import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
// 🚫 DISABLED: CopilotKit imports disabled since AI Chat feature is disabled
// import { CopilotKit } from "@copilotkit/react-core";
// import { CopilotPopup } from "@copilotkit/react-ui";
// import "@copilotkit/react-ui/styles.css";
import { UserProvider, useUser } from "./foundation/altzero_base/base/contextapi/UserContext";
import { SubscriptionProvider } from "./foundation/altzero_base/base/contextapi/SubscriptionContext";
import { PermissionsProvider } from "./foundation/altzero_base/base/contextapi/PermissionsContext";
import {
  PluginProvider,
  PluginRoutes,
  PluginProviders,
} from "./plugins/loader";
import { Toaster } from "./foundation/altzero_base/base/components/ui/toaster";
import { supabase, setupSessionRefresh } from "./foundation/altzero_base/base/utils/supabaseClient";
import { useUserRole } from "./foundation/altzero_base/base/hooks/useUserRole";
import MainLayout from "./layout/MainLayout";
import Login from "./foundation/altzero_base/base/components/auth/Login";
import Signup from "./foundation/altzero_base/base/components/auth/Signup";
import ForgotPassword from "./foundation/altzero_base/base/pages/forgot-password";
import UpdatePassword from "./foundation/altzero_base/base/pages/update-password";
import Settings from "./foundation/altzero_base/base/pages/Settings";
import AdminPortal from "./foundation/altzero_base/base/pages/AdminPortal";
import SuperAdminPortal from "./foundation/altzero_base/base/pages/SuperAdminPortal";
import { AdminProtectedRoute } from "./foundation/altzero_base/base/components/admin/AdminProtectedRoute";
import PendingApprovalPage from "./foundation/altzero_base/base/components/auth/PendingApprovalPage";
import Onboarding from "./foundation/altzero_base/base/pages/Onboarding";
// 🚫 DISABLED: FormEngine feature is disabled during plugin migration
// import FormEngineApp from "./features/formengine/FormEngineApp";
import GrapesJSEditor from "./pages/GrapesJSEditor";

// Error Boundary Component
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("App Error Boundary caught an error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center min-h-screen p-4">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-red-600 mb-4">
              Something went wrong
            </h1>
            <p className="text-gray-600 mb-4">
              The application encountered an error. Please try refreshing the
              page.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
// Root redirect component with error handling
const RootRedirect = () => {
  const { user, isLoading } = useUser();

  // Show loading while checking auth state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  // Safely redirect based on auth state
  try {
    if (user) {
      return <Navigate to="/tender" replace />;
    } else {
      return <Navigate to="/login" replace />;
    }
  } catch (error) {
    console.error("Error in root redirect:", error);
    // Fallback to login on any error
    return <Navigate to="/login" replace />;
  }
};

// Protected route component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, isLoading } = useUser();
  const { canAccessApp, needsApproval, isLoading: roleLoading } = useUserRole();
  const [onboardingChecked, setOnboardingChecked] = React.useState(false);
  const [needsOnboarding, setNeedsOnboarding] = React.useState(false);

  React.useEffect(() => {
    if (!user?.id) return;
    supabase
      .from('base_profiles')
      .select('is_onboarded')
      .eq('id', user.id)
      .single()
      // Two-argument then, not .then().catch(): the Supabase query builder is a
      // PromiseLike, which has no .catch — so the failure path here was a type
      // error and, at runtime, an unhandled rejection that left the app stuck
      // with onboardingChecked false.
      .then(
        ({ data }) => {
          setNeedsOnboarding(data?.is_onboarded === false);
          setOnboardingChecked(true);
        },
        () => setOnboardingChecked(true),
      );
  }, [user?.id]);

  if (isLoading || roleLoading || (user && !onboardingChecked)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (needsOnboarding) {
    return <Navigate to="/onboarding" replace />;
  }

  // If user needs approval, show pending approval page
  if (needsApproval) {
    return <PendingApprovalPage />;
  }

  // If user cannot access app (disapproved), redirect to login
  if (!canAccessApp) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

const AppRoutes = () => {
  const { user, isLoading } = useUser();

  useEffect(() => {
    let cleanup: (() => void) | undefined;

    const initializeSession = async () => {
      try {
        cleanup = await setupSessionRefresh();

        const { data } = await supabase.auth.getSession();
        console.log("Current session:", data.session ? "Active" : "None");
      } catch (error) {
        console.error("Session initialization failed:", error);
      }
    };

    initializeSession();

    // Cleanup on unmount
    return () => {
      if (cleanup) {
        cleanup();
      }
    };
  }, []); // Empty dependency array - only run once on mount

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={user ? <Navigate to="/tender" replace /> : <Login />}
      />
      <Route
        path="/signup"
        element={user ? <Navigate to="/tender" replace /> : <Signup />}
      />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/update-password" element={<UpdatePassword />} />
      <Route path="/onboarding" element={<Onboarding />} />

      {/* Protected routes */}
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminProtectedRoute allowOrgAdmin={true}>
              <AdminPortal />
            </AdminProtectedRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/super-admin"
        element={
          <ProtectedRoute>
            <AdminProtectedRoute>
              <SuperAdminPortal />
            </AdminProtectedRoute>
          </ProtectedRoute>
        }
      />

      {/* 🚫 DISABLED: FormEngine Routes - Feature disabled during plugin migration */}
      {/*
      <Route
        path="/formengine/*"
        element={
          <ProtectedRoute>
            <AdminProtectedRoute>
              <FormEngineApp />
            </AdminProtectedRoute>
          </ProtectedRoute>
        }
      />
      */}

      {/* GrapesJS Editor Route - Visual Web Builder */}
      <Route
        path="/tender/resource-library/document-editor"
        element={
          <ProtectedRoute>
            <GrapesJSEditor />
          </ProtectedRoute>
        }
      />

      {/* Handle root route - more robust handling */}
      <Route path="/" element={<RootRedirect />} />

      {/* 🆕 Plugin Routes - All plugins handled dynamically (includes 404 fallback) */}
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <PluginRoutes />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

const App = () => {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <UserProvider>
          <SubscriptionProvider>
            <PermissionsProvider>
            <PluginProvider>
              <PluginProviders>
                {/* 🚫 DISABLED: CopilotKit disabled since AI Chat feature is disabled */}
                {/*
                <CopilotKit
                  runtimeUrl={`${
                    import.meta.env.VITE_API_URL || "http://localhost:3001"
                  }/api/aichat`}
                  headers={{
                    "x-api-key": import.meta.env.VITE_API_KEY || "scopingai",
                  }}
                  showDevConsole={import.meta.env.DEV || false}
                >
                */}
                  <div className="relative min-h-screen">
                    <MainLayout>
                      <AppRoutes />
                    </MainLayout>

                    {/* 🚫 DISABLED: CopilotPopup disabled since AI Chat feature is disabled */}
                    {/*
                    <div className="fixed bottom-4 right-4 z-50">
                      <CopilotPopup
                        instructions="You are AltZero AI, an intelligent assistant for the AltZero platform. You can help users navigate the platform, answer questions, and provide assistance with various tasks."
                        labels={{
                          title: "AltZero Assistant",
                          initial:
                            "Hi! I'm your AltZero assistant. How can I help you today?",
                        }}
                      />
                    </div>
                    */}

                    <Toaster />
                  </div>
                {/* </CopilotKit> */}
              </PluginProviders>
            </PluginProvider>
          </PermissionsProvider>
          </SubscriptionProvider>
        </UserProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
};

export default App;
